import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  Link,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { putClient } from "../API/clientAPI";
import { getJobOrders, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import SignatureCanvas from "react-signature-canvas";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Import S3 configurations and bucket names
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

function SubmitServiceReport() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    appointment_type: "",
    treatment_type: "",
    pest_problem: "",
    inspection_report: [],
    services: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    start_time: "",
    end_time: "",
    // accomplished_by_technician: 'S',
    accomplished_by_technician: "",
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          treatment_type: data?.treatment_type || "",
          pest_problem: data?.pest_problem || "",
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          end_time: data?.end_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("")

  // Add states for button enable/disable conditions
  const [isReceiptReady, setIsReceiptReady] = useState(true); // Assume true unless conditions apply
  const [isReportReady, setIsReportReady] = useState(true);

  //for urls
  // const links = {
  //     reports_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
  //     certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
  //     receipt_termite_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receiptTermite.pdf`,
  //     receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`
  // };

  //downloading json
  const downloadJSON = (data, filename = "parameters.json") => {
    const jsonString = JSON.stringify(data, null, 2); // Convert JSON to string with pretty formatting
    const blob = new Blob([jsonString], { type: "application/json" }); // Create a Blob
    const url = URL.createObjectURL(blob); // Create a URL for the Blob
    const link = document.createElement("a"); // Create a link element
    link.href = url;
    link.download = filename; // Set the download file name
    link.click(); // Programmatically click the link to trigger download
    URL.revokeObjectURL(url); // Clean up the URL after download
  };

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleAreaChange = (event) => {
    setEditedClient((prev) => ({
      ...prev,
      addresses: prev.addresses.map((address) =>
        address.id === jobOrder?.client_mapping.addressId
          ? { ...address, area: event.target.value }
          : address
      ),
    }));
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const inspectionPestColumns = [
    "Roaches",
    "Mosq",
    "Rodents",
    "Flies",
    "Ants",
    "Others",
  ];
  const inspectionRows = Array.from({ length: 10 }, (_, index) => ({
    id: index + 1,
  })); // Example rows

  const [pestCount, setPestCount] = useState(
    inspectionRows.map(() => ({
      Roaches: "",
      Mosq: "",
      Rodents: "",
      Flies: "",
      Ants: "",
      Others: "",
    }))
  );

  const getPestTypeTotal = (pestType) => {
    return pestCount.reduce((total, row) => {
      return total + (parseInt(row[pestType], 10) || 0); // Convert to integer and handle empty values as 0
    }, 0);
  };

  const getGrandTotal = () => {
    let grandTotal = 0; // Initialize grand total to 0
    // Loop through each row in the pestCount array
    for (const pestType of inspectionPestColumns) {
      const pestTypeTotal = getPestTypeTotal(pestType);
      grandTotal += pestTypeTotal;
    }

    return grandTotal; // Return the total sum
  };

  const handlePestChange = (rowIndex, pestType, value) => {
    const newValue = value === "" ? "" : parseInt(value, 10); // Convert to number, default to 0 if empty
    setPestCount((prevData) => {
      const updatedData = [...prevData];
      updatedData[rowIndex][pestType] = isNaN(newValue) ? "" : newValue;
      // Calculate the total for the row
      updatedData[rowIndex].Total = inspectionPestColumns.reduce(
        (sum, pest) => sum + (updatedData[rowIndex][pest] || 0),
        0
      );
      return updatedData;
    });
  };

  const competencies = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection",
    "Basic Safety",
    "Driving 2 Wheels",
    "Driving 4 Wheels",
  ];
  const specializationGPC = [
    "Rodent",
    "Baiting",
    "Cockroach",
    "Fly Control",
    "Ant Control",
    "Larviciding",
  ];
  const specializationTermite = [
    "Compre",
    "AG",
    "IG",
    "IC",
    "Spraying",
    "Soil Poisoning",
  ];

  const job_types = [
    "GPC",
    "Fogging",
    "Monitoring",
    "Deep Cleaning",
    "QTM",
    "Heat Treatment",
    "Vacuuming",
    "Termite Control",
    "Comprehensive Termite",
    "Termite Baiting",
    "Termite Spot Spraying",
    "AG Installation",
    "Phase 1 and Phase 2",
    "QPMS",
    "Ant Control",
    "Cockroach Control",
    "Mosquito Control",
    "Cockroach Gel Bait/Powder",
    "Basic Rodent Control",
    "Intensive Rodent Control",
    "Termite Inspection",
    "Disinfection",
    "Billing",
    "Collection",
    "Deliver Service Invoice",
    "Deliver Certificate",
    "QuickCert",
    "Ocular",
    "Rodent Monitoring",
    "Daily Monitoring",
    "Logistics",
    "ILT Monitoring",
    "Facility Audit",
    "Meeting/Seminar",
    "QA Visit",
    "Termipore Installation",
    "In2Care Installation",
    "Exterra Inground",
    "Delivery of Materials",
    "Backjob"
  ]

  const job_description = [
    "Spraying",
    "Misting",
    "Fogging",
    "Mistblower",
    "Rodent Trapping",
    "Rodent Monitoring",
    "Logistics (Billing, Collection, Delivery)",
    "AG Installation",
    "Comprehensive Termite Control",
    "Termatrac Monitoring",
    "Wiping",
    "Disinfection",
    "UV Light",
    "Cold Fogging",
    "AG Monitoring",
    "Powder/Gel Baiting",
    "Soil Poisoning",
    "AG Pullout",
    "QA Visit/Facility Audit",
    "GPC with Blower and Vacuum",
    "Termipore, In2Care, or Device Installation"
  ]

  const legendTable = [
    {
      finding: "1 Presence of foul odor",
      recommendation: "A Keep areas clean and dry",
    },
    {
      finding: "2 Trash bins left uncovered",
      recommendation: "B Keep trash bins covered",
    },
    {
      finding: "3 Dirt/Organic Matter Present",
      recommendation: "C Dispose Garbage Daily",
    },
    {
      finding: "4 Cracks and Crevices",
      recommendation: "D Seal / Caulking of Cracks and Crevices",
    },
    {
      finding: "5 Pest Entry Point / Open Windows",
      recommendation: "E Keep doors/windows closed",
    },
    {
      finding: "6 Stagnant Water / Leaking Pipes",
      recommendation: "F Fix leaking pipes and gutters",
    },
    {
      finding: "7 Build-up of Old Stocks",
      recommendation: "G Organize Piling and Storage",
    },
    {
      finding: "8 Closed Room or Area Inaccessible",
      recommendation: "H Make room available for inspection",
    },
    {
      finding: "9 Active termites found",
      recommendation: "I Install screens/weather strip",
    },
  ];

  const [services, setServices] = useState([
    {
      id: 1,
      activity: "Inspection",
      service: "Initial GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 2,
      activity: "Monitoring",
      service: "Comprehensive GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 3,
      activity: "Spraying",
      service: "Disinfection Treatment",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 4,
      activity: "Misting",
      service: "Exterra Colony Elimination",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 5,
      activity: "Gel Baiting",
      service: "Comprehensive Termite",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 6,
      activity: "Glue Trap",
      service: "Rodent Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 7,
      activity: "Rodent Bait",
      service: "Mosquito Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 8,
      activity: "Rat Cage",
      service: "Cockroach Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 9,
      activity: "Wiping",
      service: "Audit / Follow-up",
      activityChecked: false,
      serviceChecked: false,
    },
  ]);

  const handleServicesCheckboxChange = (id, checkboxType) => {
    setServices((prevServices) =>
      prevServices.map((service) =>
        service.id === id
          ? { ...service, [checkboxType]: !service[checkboxType] }
          : service
      )
    );
  };

  const [chemical_used, setChemicalUsed] = useState([
    { label: "Spraying", chemical: "", dilution: "" },
    { label: "Misting", chemical: "", dilution: "" },
    { label: "Trapping", chemical: "", dilution: "" },
    { label: "Rodent Bait", chemical: "", dilution: "" },
    { label: "Gel Bait", chemical: "", dilution: "" },
    { label: "Others", chemical: "", dilution: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const [equipment_used, setEquipmentUsed] = useState([
    { label: "Sprayer", usage: false },
    { label: "Misting Machine", usage: false },
    { label: "Fogging Machine", usage: false },
    { label: "Power Sprayer", usage: false },
    { label: "Others", usage: false },
  ]);

  const handleEquipmentCheckboxChange = (index) => {
    const newEquipmentUsed = [...equipment_used];
    newEquipmentUsed[index].usage = !newEquipmentUsed[index].usage;
    setEquipmentUsed(newEquipmentUsed);
  };

  const [service_report_payment_status, setServiceReportPaymentStatus] =
    useState([
      { label: "Paid", selection: false },
      { label: "COD", selection: false },
      { label: "For Collection", selection: false },
    ]);

  const [service_report_payment_method, setServiceReportPaymentMethod] = useState([
    { label: "Cash", selection: false },
    { label: "Check", selection: false },
    { label: "Free", selection: false },
  ]);

  // Handler for payment status
  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = service_report_payment_status.map(
      (status, index) => ({
        ...status,
        selection: index === statusIndex ? !status.selection : false,
      })
    );
    setServiceReportPaymentStatus(newStatusOptions);
  };

  // Handler for payment method
  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = service_report_payment_method.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setServiceReportPaymentMethod(newMethodOptions);
  };

  // Fetch and organize technician data on component mount
  // useEffect(() => {
  //     const fetchTechnicians = async () => {
  //         const technicianData = await getTechnicians();
  //         if (technicianData) {
  //             setTechnicians(technicianData);

  //         }
  //     };
  //     fetchTechnicians();
  // }, []);

  // const [jobOrders, setJobOrders] = useState([]);
  // const [restDayConflict, setRestDayConflict] = useState([]);
  // const [jobOrderConflict, setJobOrderConflict] = useState([]);

  // useEffect(() => {
  //     const getDayOfWeek = (dateString) => {
  //         const date = new Date(dateString);
  //         const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  //         return daysOfWeek[date.getDay()];
  //     };

  //     // Detect rest day conflicts
  //     const detectRestDayConflicts = () => {
  //         if (!jobOrderParameters.start_date) {
  //             setRestDayConflict([]);
  //             return;
  //         }

  //         const dayOfWeek = getDayOfWeek(jobOrderParameters.start_date);
  //         const conflicts = technicians
  //             .filter((technician) => technician.rest_day.includes(dayOfWeek))
  //             .map((technician) => technician.id);
  //         setRestDayConflict(conflicts);
  //     };

  //     // Detect job order schedule conflicts
  //     const detectJobOrderConflicts = () => {
  //         if (!jobOrderParameters.start_date || !jobOrderParameters.start_time || !jobOrderParameters.end_date || !jobOrderParameters.end_time) {
  //             setJobOrderConflict([]);
  //             return;
  //         }

  //         const startDateTime = new Date(`${jobOrderParameters.start_date}T${jobOrderParameters.start_time}`);
  //         const endDateTime = new Date(`${jobOrderParameters.end_date}T${jobOrderParameters.end_time}`);
  //         const jobOrderConflictSet = jobOrders.reduce((conflicts, existingJob) => {
  //             const existingStartDateTime = new Date(`${existingJob.start_date}T${existingJob.start_time}`);
  //             const existingEndDateTime = new Date(`${existingJob.end_date}T${existingJob.end_time}`);

  //             const isOverlap =
  //                 (startDateTime >= existingStartDateTime && startDateTime < existingEndDateTime) ||
  //                 (endDateTime > existingStartDateTime && endDateTime <= existingEndDateTime) ||
  //                 (startDateTime <= existingStartDateTime && endDateTime >= existingEndDateTime);

  //             if (isOverlap) {
  //                 existingJob.technicians_mapping.forEach((technician) => {
  //                     conflicts.add(technician.id);
  //                 });
  //             }

  //             return conflicts;
  //         }, new Set());

  //         setJobOrderConflict([...jobOrderConflictSet]);
  //     };

  //     detectRestDayConflicts();
  //     detectJobOrderConflicts();
  // }, [
  //     jobOrderParameters.start_date,
  //     jobOrderParameters.start_time,
  //     jobOrderParameters.end_date,
  //     jobOrderParameters.end_time,
  //     technicians,
  //     jobOrders,
  // ]);

  // const handleAssignTechnician = (event, technician) => {
  //     const isChecked = event.target.checked;

  //     if (isChecked) {
  //         // Add technician to technicians_mapping if checked
  //         setjobOrderParameters((prev) => ({
  //             ...prev,
  //             technicians_mapping: [...prev.technicians_mapping, technician]
  //         }));
  //     } else {
  //         // Remove technician from technicians_mapping if unchecked
  //         setjobOrderParameters((prev) => ({
  //             ...prev,
  //             technicians_mapping: prev.technicians_mapping.filter(
  //                 (t) => t.id !== technician.id
  //             )
  //         }));
  //     }
  // };

  // Temporarily store the data URL
  // Save Accomplished By signature and upload to S3
  const saveAndUploadAccomplishedSignature = async () => {
    try {
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );

      // Update the state with the signature URL
      setAccomplishedSignatureURL(url);
      console.log("Accomplished Signature URL:", url);

      // Display a success message
      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      // Handle errors and show an error message
      setErrorMessage("Error saving signature. Please try again.");
    }
    setIsLoading(false);
  };

  // Clear the Accomplished By signature without affecting the button states
  const clearAccomplishedSignature = () => {
    if (accomplishedBySignatureRef.current) {
      accomplishedBySignatureRef.current.clear();
      setAccomplishedSignatureURL("");
    }
  };

  // Save Acknowledged By signature and upload to S3
  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // Update the state with the signature URL
      setAcknowledgedSignatureURL(url);
      console.log("Acknowledged Signature URL:", url);

      // Display a success message
      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  // Clear the Acknowledged By signature
  const clearAcknowledgedSignature = () => {
    if (acknowledgedBySignatureRef.current) {
      acknowledgedBySignatureRef.current.clear();
      setAcknowledgedSignatureURL("");
    }
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    const canvas = signatureRef.current.getTrimmedCanvas();
    const resizedCanvas = document.createElement("canvas");
    const size = 200;

    resizedCanvas.width = size;
    resizedCanvas.height = size;

    const ctx = resizedCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, 0, size, size);

    const blob = await new Promise((resolve) =>
      resizedCanvas.toBlob(resolve, "image/png")
    );
    const fileName = `${jobOrderId}-signature-${type}.png`; // Set fileName based on type

    // Upload resized image to S3 bucket
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: `${fileName}`, // Organize files in a subdirectory
      Body: blob,
      ContentType: "image/png",
      //ACL: 'public-read',
    };
    console.log("S3 Upload Parameters:", params); // Debugging

    try {
      const s3Response = await s3TempBucketUploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`; // Return S3 URL
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  };

  const handleGeneratePDF = async (type) => {
    // Merge contact details with jobOrderParameters
    const pdfData = {
      ...jobOrderParameters,
      // contactFirstName,
      // contactLastName,
      // contactNumber,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
    };

    console.log("Job Order Parameters:", pdfData);

    try {
      setIsLoading(true);
      const pdfBlob =
        type === "invoice"
          ? await generateReceipts(pdfData)
          : await generateReports(pdfData);
      if (!pdfBlob) {
        throw new Error("Failed to generate PDF blob.");
      }
      const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
      console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

      // Prepare update data with the correct link field
      const updateData = {
        [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
      };

      // Update DynamoDB with the S3 URL
      await patchJobOrderLink(jobOrder.id, updateData);
      console.log(`DynamoDB updated with ${type} URL:`, s3Url);
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    console.log("Generated PDF Blob:", pdfBlob);

    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader =
      type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: `${fileName}`,
      Body: pdfBlob,
      ContentType: "application/pdf",
    };

    console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

    try {
      await s3Uploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  };

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async () => {
    const generateAttachmentLinks = (links, hasTermiteControl) => {
      // Determine which keys to use for the links
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        reports_url: `${baseUrl}/${jobOrder.id}_report.pdf`,
        certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url: jobOrder.job_types.includes("termite_control")
          ? `${baseUrl}/${jobOrder.id}_final_receiptTermite.pdf`
          : `${baseUrl}/${jobOrder.id}_final_receipt.pdf`,
      };
    };

    // Generate the attachment links
    const attachmentLinks = generateAttachmentLinks();

    // Create the HTML list items from the links
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key
          .replace(/_url$/, "")
          .replace(/_/g, " ")
          .toUpperCase(); // Remove '_url' and format
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            <body>            
            <p>Sir/Ma'am,</p>
            <p></p>
            <p>Good day!</p>
            <p></p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on  ${formattedDate}.</p>
            <p>We thank you and please let us know if have any questions.</p>
            <p></p>            
            <p>Sincerely,</p>
            <p>Boaz Pest Control - Accounts Team</p>
            <ul>
              ${attachmentLinksHTML}
            </ul>
            <hr />                     
          </body>            
          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;

    const lambdaPayload = {
      // from: "boaz-billing@maestrosystemsinc.com",
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email, "diongzon.b@maestrosystemsinc.com"],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      await sendEmailQueue(lambdaPayload);
      setSuccessMessage("Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage("Failed to send email.");
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true); // Set loading to true

    const inspection_report = inspectionRows.map((row, rowIndex) => {
      const rowData = {};

      // Get the location
      rowData.location =
        document.querySelector(`input[name="location-${rowIndex}"]`)?.value ||
        "";

      // Collect all pest counts
      rowData.pests = inspectionPestColumns.reduce(
        (acc, pestType, colIndex) => {
          const pestValue =
            document.querySelector(`input[name="pest-${rowIndex}-${pestType}"]`)
              ?.value || 0;
          acc[pestType] = parseInt(pestValue, 10) || 0;
          return acc;
        },
        {}
      );

      // Get the total
      rowData.total =
        document.querySelector(`input[name="total-${rowIndex}"]`)?.value || 0;

      // Get findings and recommendations
      rowData.findings =
        document.querySelector(`input[name="findings-${rowIndex}"]`)?.value ||
        "";
      rowData.recommendation =
        document.querySelector(`input[name="recommendation-${rowIndex}"]`)
          ?.value || "";

      return rowData;
    });

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      inspection_report,
      services,
      chemical_used,
      equipment_used,
      service_report_payment_status,
      service_report_payment_method,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
      acknowledgedClientSignatory: acknowledgedClientSignatory,
      // analysis
      // temp comment out for testing
      service_report_status: "Done",
      technician_signatory: technicianSignatory
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // Pass both `jobOrder` and `jobOrderParameters` to the API
      const payload = {
        jobOrder: { ...jobOrder },
        updatedJobOrderParameters: { ...updatedJobOrderParameters },
      };

      // Convert booleans in the payload
      const convertedPayload = convertBooleans(payload);
      //downloadJSON(convertedPayload, "jobOrderParameters.json");

      //code for pdf
      // analysis
      const response = await submitDocumentFieldMapper(convertedPayload);
      console.log("Job order parameters submitted successfully:", response);

      // Added logic to set links based on competencies

      // Check if "Termite Control" is included in jobOrder.competencies
      const hasTermiteControl =
        jobOrder?.job_types?.includes("termite_control");

      // Dynamically set links based on competencies
      setLinks(
        hasTermiteControl
          ? {
            reports_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
            certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
            receipt_termite_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receiptTermite.pdf`,
          }
          : {
            reports_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
            certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
            receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
          }
      );

      // Send the email after updating links

      setSuccessMessage(
        "Service Report and documents and generated successfully! You may view the documents now!"
      );

      setIsLoading(false);
      // analysis
      await handleSendEmail();
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }

    // analysis
    await putJobOrder(jobOrder.id, updatedJobOrderParameters);
    await putClient(id, putClientParameters);


    // not included await putClientAddress(jobOrderParameters.clientAddressId, { area: jobOrderParameters.area })
    // setIsLoading(false); // Set loading to false
    //navigate('/pending-service-reports');
  }

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit} className="job-order-form">
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) =>
                          handleJObOrderChange("status", e.target.value)
                        }
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={12} xl={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address.house_unit_street || ""
                              }, ${jobOrder?.client?.address.barangay || ""
                              }, ${jobOrder?.client?.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12} xl={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""
                          }`
                          : ""
                      } // Display the contact's full name or an empty string if not available
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder?.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact?.number
                          ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                          : ""
                      } // Display the contact number or an empty string if not available
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Job Type
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12}>
                    <TextField
                      label="Job Type"
                      variant="outlined"
                      value={
                        jobOrder?.job_type
                          ? jobOrder.job_type.charAt(0).toUpperCase() +
                            jobOrder.job_type.slice(1)
                          : ""
                      }
                      disabled
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Job Type
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {job_types.map((option) => {
                  const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                  return (
                    <FormControlLabel
                      key={formattedOption}
                      control={
                        <Checkbox
                          name={formattedOption}
                          checked={(jobOrder?.job_types || []).includes(formattedOption)}
                          disabled
                        />
                      }
                      label={option}
                    />
                  );
                })}
              </CardContent>

            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Job Description
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {job_description.map((option) => {
                  const formattedOption = option
                    .toLowerCase()
                    .replace(/\s+/g, "_");
                  return (
                    <FormControlLabel
                      key={formattedOption}
                      control={
                        <Checkbox
                          name={formattedOption}
                          checked={(jobOrder?.job_description || []).includes(formattedOption)}
                          disabled
                        />
                      }
                      label={option}
                    />

                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Competency
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {competencies.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.competencies?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                              size="small"
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          {/* <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Specialization - General Pest Control
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {specializationGPC.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.specialization?.general_pest_control?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                              size="small"
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          {/* <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Specialization - Termite
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {specializationTermite.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.specialization?.termite?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                              size="small"
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder?.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        name="appointment_type"
                        value={jobOrderParameters.appointment_type}
                        label="appointment_type"
                        onChange={(e) =>
                          handleJObOrderChange(
                            "appointment_type",
                            e.target.value
                          )
                        }
                      >
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        name="treatment_type"
                        value={jobOrderParameters.treatment_type}
                        label="treatment_type"
                        onChange={(e) =>
                          handleJObOrderChange("treatment_type", e.target.value)
                        }
                      >
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={8}
                            sx={{ fontWeight: "bold" }}
                          >
                            Inspection and Pest Count
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Findings
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Recommendation
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            align="center"
                            rowSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Location
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={inspectionPestColumns.length}
                            sx={{ fontWeight: "bold" }}
                          >
                            Pests
                          </TableCell>
                          <TableCell
                            align="center"
                            rowSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Total
                          </TableCell>
                          <TableCell align="center" rowSpan={2}>
                            (Select possible root cause)
                          </TableCell>
                          <TableCell align="center" rowSpan={2}>
                            (Select client's action)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          {inspectionPestColumns.map((pest, index) => (
                            <TableCell
                              key={index}
                              align="center"
                              sx={{ fontWeight: "bold" }}
                            >
                              {pest}
                            </TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: "bold" }} />
                          <TableCell sx={{ fontWeight: "bold" }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inspectionRows.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`location-${rowIndex}`}
                                sx={{ minWidth: 150 }}
                              />
                            </TableCell>
                            {inspectionPestColumns.map((pestType, colIndex) => (
                              <TableCell key={colIndex}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={pestCount[rowIndex][pestType]}
                                  onChange={(e) =>
                                    handlePestChange(
                                      rowIndex,
                                      pestType,
                                      e.target.value
                                    )
                                  }
                                  InputProps={{
                                    inputProps: { min: 0, step: 1 },
                                  }} // `step: 1` ensures whole numbers only
                                  name={`pest-${rowIndex}-${pestType}`}
                                  sx={{ minWidth: 50 }}
                                />
                              </TableCell>
                            ))}
                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                value={
                                  pestCount[rowIndex] &&
                                    pestCount[rowIndex].Total !== undefined &&
                                    pestCount[rowIndex].Total !== 0
                                    ? pestCount[rowIndex].Total
                                    : ""
                                }
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            <TableCell>
                              {/* <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`findings-${rowIndex}`}
                                sx={{ minWidth: 150 }}
                              /> */}
                              <TextField
                                select
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`findings-${rowIndex}`}
                                sx={{ minWidth: 150 }}
                                defaultValue=""
                              >
                                <MenuItem value="">
                                  None
                                </MenuItem>
                                <MenuItem value="Presence of foul odor">Presence of foul odor</MenuItem>
                                <MenuItem value="Trash bins left uncovered">Trash bins left uncovered</MenuItem>
                                <MenuItem value="Dirt/Organic Matter Present">Dirt/Organic Matter Present</MenuItem>
                                <MenuItem value="Cracks and Crevices">Cracks and Crevices</MenuItem>
                                <MenuItem value="Pest Entry Point / Open Windows">Pest Entry Point / Open Windows</MenuItem>
                                <MenuItem value="Stagnant Water / Leaking Pipes">Stagnant Water / Leaking Pipes</MenuItem>
                                <MenuItem value="Build-up of Old Stocks">Build-up of Old Stocks</MenuItem>
                                <MenuItem value="Closed Room or Area Inaccessible">Closed Room or Area Inaccessible</MenuItem>
                                <MenuItem value="Active termites found">Active termites found</MenuItem>
                              </TextField>

                            </TableCell>
                            <TableCell>
                              {/* <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`recommendation-${rowIndex}`}
                                sx={{ minWidth: 150 }}
                              /> */}
                              <TextField
                                select
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`recommendation-${rowIndex}`}
                                sx={{ minWidth: 150 }}
                                defaultValue=""
                              >
                                <MenuItem value="">
                                  None
                                </MenuItem>
                                <MenuItem value="Keep areas clean and dry">Keep areas clean and dry</MenuItem>
                                <MenuItem value="Keep trash bins covered">Keep trash bins covered</MenuItem>
                                <MenuItem value="Dispose Garbage Daily">Dispose Garbage Daily</MenuItem>
                                <MenuItem value="Seal / Caulking of Cracks and Crevices">Seal / Caulking of Cracks and Crevices</MenuItem>
                                <MenuItem value="Keep doors/windows closed">Keep doors/windows closed</MenuItem>
                                <MenuItem value="Fix leaking pipes and gutters">Fix leaking pipes and gutters</MenuItem>
                                <MenuItem value="Organize Piling and Storage">Organize Piling and Storage</MenuItem>
                                <MenuItem value="Make room available for inspection">Make room available for inspection</MenuItem>
                                <MenuItem value="Install screens/weather strip">Install screens/weather strip</MenuItem>
                              </TextField>

                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Overall Total Pests
                          </TableCell>
                          {inspectionPestColumns.map((pestType, colIndex) => (
                            <TableCell key={colIndex}>
                              <TextField
                                variant="outlined"
                                size="small"
                                value={
                                  getPestTypeTotal(pestType) === 0
                                    ? ""
                                    : getPestTypeTotal(pestType)
                                }
                                fullWidth
                                disabled
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: "bold" }}>
                            <TextField
                              variant="outlined"
                              size="small"
                              value={
                                getGrandTotal() === 0 ? "" : getGrandTotal()
                              }
                              fullWidth
                              disabled
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="left"
                            sx={{ fontWeight: "bold", verticalAlign: "top", paddingTop: "12px" }}
                          >
                            Activities Done
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontWeight: "bold", verticalAlign: "top", paddingTop: "12px" }}
                          >
                            Services Performed
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {services.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell sx={{ verticalAlign: "top", paddingTop: "8px" }}>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  checked={item.activityChecked}
                                  onChange={() =>
                                    handleServicesCheckboxChange(item.id, "activityChecked")
                                  }
                                />
                                <Typography variant="body2">{item.activity}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top", paddingTop: "8px" }}>
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  checked={item.serviceChecked}
                                  onChange={() =>
                                    handleServicesCheckboxChange(item.id, "serviceChecked")
                                  }
                                />
                                <Typography variant="body2">{item.service}</Typography>
                              </Box>
                            </TableCell>                                                    
                          </TableRow>
                        ))}
                      </TableBody>

                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Reminder Notes
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={4}
                            style={{ fontWeight: "bold" }}
                          >
                            Service Fee
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chemical_used.map((chemical, index) => (
                          <TableRow key={index}>
                            <TableCell>{chemical.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.chemical}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "chemical",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.dilution}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "dilution",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            {index < equipment_used.length ? (
                              <>
                                <TableCell>
                                  {equipment_used[index].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={equipment_used[index].usage}
                                    onChange={() =>
                                      handleEquipmentCheckboxChange(index)
                                    }
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}

                            {index === 0 && (
                              <TableCell rowSpan={6}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  value={jobOrderParameters.reminder_notes}
                                  onChange={(e) =>
                                    handleJObOrderChange(
                                      "reminder_notes",
                                      e.target.value
                                    )
                                  }
                                  multiline
                                  rows={14}
                                  sx={{ minWidth: 150 }}
                                />
                              </TableCell>
                            )}

                            {index === 0 && (
                              <TableCell rowSpan={2} colSpan={4}>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  value={jobOrderParameters.service_fee}
                                  onChange={(e) =>
                                    handleJObOrderChange(
                                      "service_fee",
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₱
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </TableCell>
                            )}
                            {index === 2 && (
                              <>
                                <TableCell align="center" colSpan={4}>
                                  Payment Status:
                                </TableCell>
                              </>
                            )}
                            {index === 3 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status[0].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status[0].selection
                                    }
                                    onChange={(e) =>
                                      handlePaymentStatusChange(0)
                                    }
                                  />
                                </TableCell>
                                <TableCell>{service_report_payment_method[0].label}</TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={service_report_payment_method[0].selection}
                                    onChange={(e) =>
                                      handlePaymentMethodChange(0)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}
                            {index === 4 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status[1].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status[1].selection
                                    }
                                    onChange={(e) =>
                                      handlePaymentStatusChange(1)
                                    }
                                  />
                                </TableCell>
                                <TableCell>{service_report_payment_method[1].label}</TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={service_report_payment_method[1].selection}
                                    onChange={(e) =>
                                      handlePaymentMethodChange(1)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}
                            {index === 5 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status[2].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status[2].selection
                                    }
                                    onChange={(e) =>
                                      handlePaymentStatusChange(2)
                                    }
                                  />
                                </TableCell>
                                <TableCell>{service_report_payment_method[2].label}</TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={service_report_payment_method[2].selection}
                                    onChange={(e) =>
                                      handlePaymentMethodChange(2)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      label="Start Date"
                      name="job_date"
                      type="date"
                      value={jobOrderParameters.job_date}
                      onChange={(e) =>
                        handleJObOrderChange("job_date", e.target.value)
                      }
                      fullWidth
                      size="small"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrderParameters.start_time}
                      onChange={(e) =>
                        handleJObOrderChange("start_time", e.target.value)
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrderParameters.end_time}
                      onChange={(e) =>
                        handleJObOrderChange("end_time", e.target.value)
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Technicians
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    {jobOrder?.technicians?.length > 0 ? (
                      jobOrder?.technicians.map((technician, index) => (
                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          key={technician.id}
                        >
                          <TextField
                            label={`Technician ${index + 1}`}
                            variant="outlined"
                            fullWidth
                            value={`${technician.firstname} ${technician.lastname}`}
                            disabled
                            size="small"
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12} md={12} lg={12} xl={12}>
                        <TextField
                          label="Technician"
                          variant="outlined"
                          fullWidth
                          value="No Technicians Available"
                          InputProps={{
                            readOnly: true,
                          }}
                          size="small"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          {/* Accomplished By and Acknowledged By Signature Section */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    {/* Service Accomplished by Section */}
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Service Accomplished by:
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {jobOrder?.technicians?.length > 0 ? (
                              jobOrder?.technicians.map((technician, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                  key={technician.id}
                                  mb={2}
                                >
                                  <TextField
                                    label={`Technician ${index + 1}`}
                                    variant="outlined"
                                    fullWidth
                                    value={`${technician.firstname} ${technician.lastname}`}
                                    disabled
                                    size="small"
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12} md={12} lg={12} xl={12}>
                                <TextField
                                  label="Technician"
                                  variant="outlined"
                                  fullWidth
                                  value="No Technicians Available"
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>                          
                          <Box
                            sx={{
                              width: canvasWidth,
                              height: ackHeight,
                              border: "2px solid #000",
                              padding: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <SignatureCanvas
                              ref={accomplishedBySignatureRef}
                              penColor="black"
                              canvasProps={{
                                width: canvasWidth,
                                height: ackHeight,
                                className: "sigCanvas",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Technician Signatory (read‑only) */}
                      {/* <Grid item xs={12}>
                        <TextField
                          label="Technician Signatory"
                          value={
                            Array.isArray(jobOrderParameters.accomplished_by_technician) &&
                            jobOrderParameters.accomplished_by_technician.length > 0
                              ? jobOrderParameters.accomplished_by_technician[0]
                              : ""
                          }
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid> */}

                      {/* Technician Signatory Dropdown */}
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="technician-signatory-label">
                            Technician Signatory
                          </InputLabel>
                          <Select
                            labelId="technician-signatory-label"
                            id="accomplished-by-technician"
                            value={technicianSignatory}
                            label="Technician Signatory"
                            onChange={e => setTechnicianSignatory(e.target.value)}
                          >
                            {jobOrder?.technicians?.map((tech) => (
                              <MenuItem
                                key={tech.id}
                                value={`${tech.firstname} ${tech.lastname}`}
                              >
                                {`${tech.firstname} ${tech.lastname}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>


                      {/* Action Buttons for Accomplished Signature */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={clearAccomplishedSignature}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveAndUploadAccomplishedSignature}
                          sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                        >
                          Upload
                        </Button>
                      </Grid>
                    </Grid>


                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Acknowledged By:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        {/* <Box
                          sx={{
                            width: {
                              xs: 200, // Width for extra small screens
                              md: 250, // Width for medium screens
                              lg: 300, // Width for large screens
                            }, // Define width matching canvas width
                            height: {
                              xs: 100, // Height for extra small screens
                              md: 125, // Height for medium screens
                              lg: 150, // Height for large screens
                            },
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: {
                                xs: 200, // Width for extra small screens
                                md: 250, // Width for medium screens
                                lg: 300, // Width for large screens
                              }, // Define width matching canvas width
                              height: {
                                xs: 100, // Height for extra small screens
                                md: 125, // Height for medium screens
                                lg: 150, // Height for large screens
                              },
                              className: "sigCanvas",
                            }}
                          />
                        </Box> */}
                        <Box
                          sx={{
                            width: ackWidth,
                            height: ackHeight,
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: ackWidth,
                              height: ackHeight,
                              className: "sigCanvas",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ marginTop: 2 }}>
                      <Grid item xs={12} md={12} lg={12}>
                        {/* <TextField
                          fullWidth
                          label="Contact Person"
                          name="Contact Person"
                          variant="outlined"
                          value={
                            jobOrder?.client?.contact
                              ? `${jobOrder?.client?.contact.firstname || ""} ${jobOrder?.client?.contact.lastname || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        /> */}
                        <TextField
                          fullWidth
                          label="Contact Person"
                          name="Contact Person"
                          variant="outlined"
                          value={acknowledgedClientSignatory}
                          onChange={(e) => setAcknowledgedClientSignatory(e.target.value)}
                          size="small"
                        />

                      </Grid>
                    </Grid>
                    {/* Action Buttons for Acknowledged Signature */}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      mt={2}
                      mb={2}
                      mr={2}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={clearAcknowledgedSignature}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FF4C4C",
                          borderColor: "#FF4C4C",
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveAndUploadAcknowledgedSignature}
                        sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                      >
                        Upload
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>

              </Card>
            </Grid>
          </Grid>
          {/* Display Receipts and Reports Links */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {links?.receipts_url ||
                    links?.receipt_termite_url ||
                    links?.certificate_url ||
                    links?.reports_url ? (
                    <>
                      {links?.receipts_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.receipts_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipts.pdf{" "}
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.receipt_termite_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.receipt_termite_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipt_Termite.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.certificate_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.certificate_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Certificate.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.reports_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.reports_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Reports.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form buttons */}

          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-service-reports")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default SubmitServiceReport;
